import { useCallback } from 'react';

import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import type { BackError } from '../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';

import type { CustomizeAllMutation } from '../types/props.type';
import type { SubmitHandler } from '../types/submit-handler.type';

interface Params {
  readonly directMutation?: CustomizeAllMutation;
  readonly handleCustomizeAllSuccess: () => void;
  readonly serviceUserId: Id;
}

interface FormikArrayActions {
  readonly customizeAllInForm: () => void;
}

export const useCustomizeAllResolvableServiceUsagesSubmit = ({
  directMutation,
  handleCustomizeAllSuccess,
  serviceUserId,
}: Readonly<Params>) => {
  const handleSubmitFactory = useCallback(
    ({ customizeAllInForm }: Readonly<FormikArrayActions>): SubmitHandler =>
      ({ memos, formikHelpers: { setSubmitting }, setFormError }) => {
        setFormError(null);

        const onError = (error: unknown) => {
          setFormError(userFriendlyErrors(error as BackError));
          setSubmitting(false);
        };

        const onSuccess = () => {
          handleCustomizeAllSuccess();
          setSubmitting(false);
        };

        if (directMutation) {
          directMutation(
            {
              serviceUserId,
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );

          return undefined;
        }

        // The direct mutation reloads the whole pricing (groups included):
        // only the form state needs the local update.
        customizeAllInForm();
        onSuccess();

        return undefined;
      },
    [serviceUserId, directMutation, handleCustomizeAllSuccess],
  );

  return { handleSubmitFactory };
};
